import React, { useState, useEffect, useRef } from "react";
import { X, CheckCircle, AlertCircle, CreditCard } from "lucide-react";
import { trackEvent, trackEcommerceEvent, formatPrice } from "../utils";
import { PincodeInput } from "./PincodeInput";

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
}

export function OrderModal({ isOpen, onClose, product }: OrderModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [paymentTimeoutMsg, setPaymentTimeoutMsg] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [details, setDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
  });
  const [orderInfo, setOrderInfo] = useState<{ orderId: string; rzpOrderId: string } | null>(null);

  const [showAdditional, setShowAdditional] = useState(false);
  const trackedFieldsRef = useRef<Set<string>>(new Set());

  const isDiscountActive = React.useMemo(() => {
    if (!product?.discountValidity || !product?.discountPrice) return false;
    const validityDate = new Date(product.discountValidity).getTime();
    const now = new Date().getTime();
    return validityDate > now;
  }, [product?.discountValidity, product?.discountPrice]);

  const activePriceStr = isDiscountActive && product?.discountPrice ? String(product.discountPrice) : String(product?.price || "0");
  const priceNum = parseInt(activePriceStr.replace(/[^0-9]/g, ""), 10);
  const activePriceFormatted = isDiscountActive && product?.discountPrice ? "₹" + priceNum.toLocaleString('en-IN') : formatPrice(product?.price || 45000);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError("");
      setFieldErrors({});
      setShowAdditional(false);
      setIsSubmitting(false);
      setOrderInfo(null);
      trackedFieldsRef.current.clear();
    } else {
      // Clear state when closed so that on next open, it is clean from the very first render!
      setDetails({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address1: "",
        address2: "",
        landmark: "",
        pincode: "",
        city: "",
        state: "",
      });
      trackedFieldsRef.current.clear();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (step === 1) {
        trackEvent("checkout_step_shipping_view", { item_name: product?.name });
      } else if (step === 2) {
        trackEvent("checkout_step_payment_view", { item_name: product?.name });
      } else if (step === 3) {
        trackEvent("checkout_step_success_view", { item_name: product?.name });
      }
    }
  }, [isOpen, step, product?.name]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  // Auto-track pincode when fully entered (6 digits)
  useEffect(() => {
    if (!isOpen) return;
    const pin = details.pincode.trim();
    if (pin.length === 6 && !pin.startsWith("0") && /^\d{6}$/.test(pin)) {
      const trackKey = "pincode_true";
      if (!trackedFieldsRef.current.has(trackKey)) {
        trackedFieldsRef.current.add(trackKey);
        trackEvent("checkout_field_completed", {
          field_name: "pincode",
          is_valid: true,
          item_name: product?.name
        });
      }
    }
  }, [details.pincode, isOpen, product?.name]);

  // Auto-track city when populated
  useEffect(() => {
    if (!isOpen) return;
    const city = details.city.trim();
    if (city) {
      const trackKey = "city_true";
      if (!trackedFieldsRef.current.has(trackKey)) {
        trackedFieldsRef.current.add(trackKey);
        trackEvent("checkout_field_completed", {
          field_name: "city",
          is_valid: true,
          item_name: product?.name
        });
      }
    }
  }, [details.city, isOpen, product?.name]);

  // Auto-track state when populated
  useEffect(() => {
    if (!isOpen) return;
    const state = details.state.trim();
    if (state) {
      const trackKey = "state_true";
      if (!trackedFieldsRef.current.has(trackKey)) {
        trackedFieldsRef.current.add(trackKey);
        trackEvent("checkout_field_completed", {
          field_name: "state",
          is_valid: true,
          item_name: product?.name
        });
      }
    }
  }, [details.state, isOpen, product?.name]);

  if (!isOpen) return null;

  const validateDetails = () => {
    let isValid = true;
    const errors: { [key: string]: string } = {};

    const nameRegex = /^[A-Za-z\s.]+$/;
    
    let fn = details.firstName.trim().replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
    if (!fn) {
      errors.firstName = "First name is mandatory.";
      isValid = false;
    } else if (fn.length < 1 || !nameRegex.test(fn) || !/[a-zA-Z]/.test(fn)) {
      errors.firstName = "Names can only contain letters, spaces, and dots.";
      isValid = false;
    }

    let ln = details.lastName.trim().replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
    if (ln && (!nameRegex.test(ln) || ln.length < 1 || !/[a-zA-Z]/.test(ln))) {
      errors.lastName = "Names can only contain letters, spaces, and dots.";
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!details.email.trim() || !emailRegex.test(details.email.trim()) || details.email.includes("..")) {
      errors.email = "Please enter a valid email address (e.g., name@example.com).";
      isValid = false;
    }

    const phoneStr = details.phone.trim();
    if (!phoneStr || phoneStr.length !== 10 || !/^[6-9]/.test(phoneStr) || /^(\d)\1{9}$/.test(phoneStr) || /^(1234567890|0987654321)$/.test(phoneStr)) {
      errors.phone = "Please enter a valid 10-digit mobile number.";
      isValid = false;
    }

    const addr1 = details.address1.trim();
    if (!addr1) {
      errors.address1 = "Address Line 1 is required.";
      isValid = false;
    } else if (addr1.length < 5 || !/[a-zA-Z]/.test(addr1) || /(.)\1{4,}/.test(addr1) || !/(\d|\/|\-|flat|plot|floor|no|block|pocket|hno|house|bldg|building)/i.test(addr1)) {
      errors.address1 = "Please include your specific House, Flat, or Block number along with the street name.";
      isValid = false;
    }
    
    const addr2 = details.address2.trim();
    if (addr2 && (!/[a-zA-Z]/.test(addr2) || /(.)\1{4,}/.test(addr2))) {
      errors.address2 = "Please enter a valid address.";
      isValid = false;
    }
    
    if (details.landmark.trim() && details.landmark.trim().length > 100) {
      errors.landmark = "Landmark is too long. Please keep it under 100 characters.";
      isValid = false;
    }

    const pin = details.pincode.trim();
    if (!pin || pin.length !== 6 || pin.startsWith("0") || /^\d{6}$/.test(pin) === false) {
      errors.pincode = "Please enter a valid 6-digit Indian Pincode.";
      isValid = false;
    }

    if (!details.city.trim()) {
      errors.city = "City is required. Please check your PIN code.";
      isValid = false;
    }

    if (!details.state.trim()) {
      errors.state = "State is required. Please check your PIN code.";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleChange = (field: keyof typeof details, value: string) => {
    let sanitized = value;
    if (field === "firstName" || field === "lastName") {
      sanitized = sanitized.replace(/[^a-zA-Z\s.]/g, "");
    } else if (field === "phone") {
      sanitized = sanitized.replace(/\D/g, "");
      if (sanitized.startsWith("91") && sanitized.length > 2 && sanitized.length <= 12) {
         if (sanitized.length > 10) sanitized = sanitized.substring(2);
      }
      if (sanitized.startsWith("0") && sanitized.length > 10) {
         sanitized = sanitized.substring(1);
      }
      sanitized = sanitized.substring(0, 10);
    } else if (field === "address1" || field === "address2" || field === "landmark" || field === "city" || field === "state") {
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s.,\-\/\#\(\)]/g, "");
    } else if (field === "pincode") {
      sanitized = sanitized.replace(/\D/g, "").substring(0, 6);
    } else if (field === "email") {
      sanitized = sanitized.replace(/\s/g, "");
    }

    setDetails(prev => ({ ...prev, [field]: sanitized }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleBlur = (field: keyof typeof details, value?: string) => {
    const currentVal = value !== undefined ? value : details[field];
    const val = currentVal.trim();
    if (details[field] !== val) {
      setDetails(prev => ({ ...prev, [field]: val }));
    }

    // Call full validation, but clear constraints for unrelated fields or just run a specific check.
    // Instead of duplicating logic, we'll wait for the user to submit for the full sweep,
    // or we can invoke our field-specific validation slice if needed.
    // Given the prompt, we should show errors for this field. Let's do a mini validation.
    
    let error = "";
    if (field === "firstName") {
       let fn = val.replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
       if (!fn) error = "First name is mandatory.";
       else if (fn.length < 1 || !/^[A-Za-z\s.]+$/.test(fn) || !/[a-zA-Z]/.test(fn)) error = "Names can only contain letters, spaces, and dots.";
    } else if (field === "lastName") {
       let ln = val.replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
       if (ln && (ln.length < 1 || !/^[A-Za-z\s.]+$/.test(ln) || !/[a-zA-Z]/.test(ln))) error = "Names can only contain letters, spaces, and dots.";
    } else if (field === "email") {
       if (!val || !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(val) || val.includes("..")) error = "Please enter a valid email address (e.g., name@example.com).";
    } else if (field === "phone") {
       if (!val || val.length !== 10 || !/^[6-9]/.test(val) || /^(\d)\1{9}$/.test(val) || /^(1234567890|0987654321)$/.test(val)) error = "Please enter a valid 10-digit mobile number.";
    } else if (field === "address1") {
       if (!val || val.length < 5 || !/[a-zA-Z]/.test(val) || /(.)\1{4,}/.test(val) || !/(\d|\/|\-|flat|plot|floor|no|block|pocket|hno|house|bldg|building)/i.test(val)) error = "Please include your specific House, Flat, or Block number along with the street name.";
    } else if (field === "address2" && val) {
       if (!/[a-zA-Z]/.test(val) || /(.)\1{4,}/.test(val)) error = "Please enter a valid address.";
    } else if (field === "landmark" && val.length > 100) {
       error = "Landmark is too long. Please keep it under 100 characters.";
    } else if (field === "pincode") {
       if (!val || val.length !== 6 || val.startsWith("0")) error = "Please enter a valid 6-digit Indian Pincode.";
    } else if (field === "city" && !val) {
       error = "City is required. Please check your PIN code.";
    } else if (field === "state" && !val) {
       error = "State is required. Please check your PIN code.";
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: error }));
    
    // Track field blur/completion event
    if (typeof window !== "undefined") {
      if (val) {
        const is_valid = !error;
        const trackKey = `${String(field)}_${is_valid}`;
        if (!trackedFieldsRef.current.has(trackKey)) {
          trackedFieldsRef.current.add(trackKey);
          trackEvent("checkout_field_completed", { 
            field_name: field, 
            is_valid: is_valid,
            item_name: product?.name 
          });
        }
      }
    }
  };

  const getUtmParams = () => {
    try {
      const storedUtms = sessionStorage.getItem("utmParams");
      if (storedUtms) return JSON.parse(storedUtms);
    } catch (e) {
      console.warn("Could not parse UTM params", e);
    }
    return {};
  };

  const handleDeliverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent("click_continue_to_payment", { item_name: product?.name });
    setError("");

    // Identify and track any fields that are currently valid but have not been tracked yet
    const fieldList: (keyof typeof details & string)[] = [
      "firstName", "lastName", "email", "phone", "address1", "address2", "landmark", "pincode", "city", "state"
    ];
    
    const errors: { [key: string]: string } = {};
    const nameRegex = /^[A-Za-z\s.]+$/;
    
    const fn = details.firstName.trim().replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
    if (!fn || fn.length < 1 || !nameRegex.test(fn) || !/[a-zA-Z]/.test(fn)) errors.firstName = "err";

    const ln = details.lastName.trim().replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, "");
    if (ln && (!nameRegex.test(ln) || ln.length < 1 || !/[a-zA-Z]/.test(ln))) errors.lastName = "err";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!details.email.trim() || !emailRegex.test(details.email.trim()) || details.email.includes("..")) errors.email = "err";

    const phoneStr = details.phone.trim();
    if (!phoneStr || phoneStr.length !== 10 || !/^[6-9]/.test(phoneStr) || /^(\d)\1{9}$/.test(phoneStr) || /^(1234567890|0987654321)$/.test(phoneStr)) errors.phone = "err";

    const addr1 = details.address1.trim();
    if (!addr1 || addr1.length < 5 || !/[a-zA-Z]/.test(addr1) || /(.)\1{4,}/.test(addr1) || !/(\d|\/|\-|flat|plot|floor|no|block|pocket|hno|house|bldg|building)/i.test(addr1)) errors.address1 = "err";
    
    const addr2 = details.address2.trim();
    if (addr2 && (!/[a-zA-Z]/.test(addr2) || /(.)\1{4,}/.test(addr2))) errors.address2 = "err";
    
    if (details.landmark.trim() && details.landmark.trim().length > 100) errors.landmark = "err";

    const pin = details.pincode.trim();
    if (!pin || pin.length !== 6 || pin.startsWith("0") || /^\d{6}$/.test(pin) === false) errors.pincode = "err";

    if (!details.city.trim()) errors.city = "err";
    if (!details.state.trim()) errors.state = "err";

    fieldList.forEach(field => {
      const val = details[field].trim();
      const isFieldValid = !errors[field];
      const shouldTrack = ["lastName", "address2", "landmark"].includes(field) ? !!val : true;

      const trackKey = `${String(field)}_true`;
      if (shouldTrack && isFieldValid && !trackedFieldsRef.current.has(trackKey)) {
        trackedFieldsRef.current.add(trackKey);
        trackEvent("checkout_field_completed", {
          field_name: field,
          is_valid: true,
          item_name: product?.name
        });
      }
    });

    if (!validateDetails()) {
      trackEvent("checkout_step_shipping_error", { item_name: product?.name, error_type: "validation_failed" });
      setError("Please fix the errors below to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/orders/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...details,
          productId: product?.id || "",
          productName: product?.name || "Unknown Product",
          productPrice: activePriceFormatted,
          productImage: product?.image || "",
        }),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to create order");
      }
      trackEcommerceEvent("add_shipping_info", product, { value: priceNum });
      setOrderInfo({ orderId: "", rzpOrderId: data.rzpOrderId });
      setStep(2);
    } catch (err: any) {
      trackEvent("checkout_create_order_error", { item_name: product?.name, error: err.message });
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = async () => {
    trackEvent("click_pay_now", { item_name: product?.name });
    if (!orderInfo?.rzpOrderId || orderInfo.rzpOrderId === "order_mock") {
      // Simulate payment success if no razorpay credentials configured
      verifyPayment("simulated_payment_id", "simulated_signature", orderInfo?.rzpOrderId || "");
      return;
    }

    const priceNum = parseFloat(activePriceStr.replace(/[^0-9.]/g, ""));

    const loadRazorpay = () => {
      return new Promise((resolve) => {
        if ((window as any).Razorpay) {
          resolve(true);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      setError("Payment system failed to load. Please check your internet connection.");
      return;
    }

    const options = {
      key: import.meta.env.VITE_RAZORPAY_KEY_ID || "mock_key_will_be_overridden_when_real",
      amount: Math.round((priceNum || 0) * 100),
      currency: "INR",
      name: "Kayoki",
      description: "Order Payment",
      order_id: orderInfo.rzpOrderId,
      handler: function (response: any) {
        verifyPayment(response.razorpay_payment_id, response.razorpay_signature, orderInfo.rzpOrderId);
      },
      prefill: {
        name: `${details.firstName} ${details.lastName}`.trim(),
        contact: details.phone,
        email: details.email,
      },
      theme: { color: "#000000" },
      modal: {
        ondismiss: async function() {
          trackEvent("payment_abandoned", { item_name: product?.name });
          try {
            await fetch("/api/orders/payment-failed", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                 amount: Math.round((priceNum || 0) * 100),
                 razorpay_order_id: orderInfo.rzpOrderId,
                 razorpay_payment_id: "",
                 deliveryDetails: details,
                 utmParams: getUtmParams(),
                 status: "Abandoned"
              })
            });
          } catch (e) {
            console.error("Could not record payment abandoned", e);
          }
        }
      }
    };

    const rzp = new (window as any).Razorpay(options);
    trackEcommerceEvent("add_payment_info", product, { value: priceNum });
    rzp.on("payment.failed", async function (response: any) {
      trackEvent("payment_failed", { item_name: product?.name, error: response.error.description });
      setError("Payment failed: " + response.error.description);
      try {
        await fetch("/api/orders/payment-failed", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
             amount: Math.round((priceNum || 0) * 100),
             razorpay_order_id: response?.error?.metadata?.order_id || orderInfo.rzpOrderId,
             razorpay_payment_id: response?.error?.metadata?.payment_id || "",
             deliveryDetails: details,
             utmParams: getUtmParams(),
             status: response?.error ? "Failed" : "Abandoned"
          })
        });
      } catch (e) {
        console.error("Could not record payment failure", e);
      }
    });
    rzp.open();
  };

  const verifyPayment = async (paymentId: string, signature: string, rzpOrderId: string) => {
    setIsSubmitting(true);
    setPaymentTimeoutMsg("");

    const timeoutId = setTimeout(() => {
      setPaymentTimeoutMsg(
        "Your payment verification is taking longer than usual. Please do not close this window. If the payment is successful, your order will be confirmed shortly. If the payment fails or times out, but your amount was deducted, it will be automatically refunded to your original payment method in 5-7 business days."
      );
    }, 60000);

    try {
      const response = await fetch("/api/orders/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          razorpay_order_id: rzpOrderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          amount: Math.round((priceNum || 0) * 100),
          deliveryDetails: details,
          utmParams: getUtmParams(),
          productDetails: {
            productId: product?.id || "",
            productName: product?.name || "Unknown Product",
            productPrice: activePriceFormatted,
            productImage: product?.image || "",
          }
        }),
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Payment verification failed");
      }
      trackEcommerceEvent("purchase", product, {
        transaction_id: data.customOrderId,
        value: priceNum
      });
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Purchase", {
          value: priceNum,
          currency: "INR"
        });
      }
      setOrderInfo({ orderId: data.customOrderId, rzpOrderId: rzpOrderId });
      setStep(3);
    } catch (err: any) {
      trackEvent("checkout_verify_payment_error", { item_name: product?.name, error: err.message });
      setError(err.message);
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-black">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold uppercase tracking-tight">
            {step === 1 && "Order Details"}
            {step === 2 && "Payment"}
            {step === 3 && "Order Confirmed"}
          </h2>
          <button
            onClick={() => {
              if (step < 3) {
                trackEvent("abandon_checkout", { step: step, item_name: product?.name });
              }
              onClose();
            }}
            className="text-gray-400 hover:text-black transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar" ref={scrollRef}>
          {step === 1 && (
            <form onSubmit={handleDeliverySubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                    First Name*
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    placeholder="e.g. John"
                    aria-label="First Name"
                    value={details.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    onBlur={(e) => handleBlur("firstName", e.target.value)}
                    className={`w-full bg-[#f8f8f8] border ${fieldErrors.firstName ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                  />
                  {fieldErrors.firstName && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    maxLength={50}
                    placeholder="e.g. Doe"
                    aria-label="Last Name"
                    value={details.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    onBlur={(e) => handleBlur("lastName", e.target.value)}
                    className={`w-full bg-[#f8f8f8] border ${fieldErrors.lastName ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                  />
                  {fieldErrors.lastName && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                    Email ID*
                  </label>
                  <input
                    type="email"
                    required
                    maxLength={254}
                    placeholder="e.g. john.doe@example.com"
                    aria-label="Email"
                    value={details.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    onBlur={(e) => handleBlur("email", e.target.value)}
                    className={`w-full bg-[#f8f8f8] border ${fieldErrors.email ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                  />
                  {fieldErrors.email && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                    Phone Number*
                  </label>
                  <input
                    type="tel"
                    required
                    maxLength={10}
                    placeholder="e.g. 9876543210"
                    aria-label="Phone Number"
                    value={details.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    onBlur={(e) => handleBlur("phone", e.target.value)}
                    className={`w-full bg-[#f8f8f8] border ${fieldErrors.phone ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                  />
                  {fieldErrors.phone && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                  Address Line 1*
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  placeholder="e.g. House No. 123, Street 4"
                  aria-label="Address Line 1"
                  value={details.address1}
                  onChange={(e) => handleChange("address1", e.target.value)}
                  onBlur={(e) => handleBlur("address1", e.target.value)}
                  className={`w-full bg-[#f8f8f8] border ${fieldErrors.address1 ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                />
                {fieldErrors.address1 && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.address1}</p>}
              </div>

              {!showAdditional ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdditional(true)}
                    className="text-xs font-semibold text-[#b89558] uppercase tracking-widest text-left hover:text-[#9e804a] transition-colors"
                  >
                    + Additional Details
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="e.g. Sector 1"
                      aria-label="Address Line 2"
                      value={details.address2}
                      onChange={(e) => handleChange("address2", e.target.value)}
                      onBlur={(e) => handleBlur("address2", e.target.value)}
                      className={`w-full bg-[#f8f8f8] border ${fieldErrors.address2 ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                    />
                    {fieldErrors.address2 && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.address2}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Landmark
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="e.g. Near City Mall"
                      aria-label="Landmark"
                      value={details.landmark}
                      onChange={(e) => handleChange("landmark", e.target.value)}
                      onBlur={(e) => handleBlur("landmark", e.target.value)}
                      className={`w-full bg-[#f8f8f8] border ${fieldErrors.landmark ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
                    />
                    {fieldErrors.landmark && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{fieldErrors.landmark}</p>}
                  </div>
                </>
              )}

              <PincodeInput
                pincode={details.pincode}
                city={details.city}
                state={details.state}
                error={fieldErrors.pincode}
                cityError={fieldErrors.city}
                stateError={fieldErrors.state}
                onChange={(field, value) => {
                  setDetails((prev) => ({ ...prev, [field]: value }));
                  if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: "" }));
                }}
                onErrorChange={(error) => setFieldErrors((prev) => ({ ...prev, pincode: error }))}
                onFieldBlur={(field) => handleBlur(field as keyof typeof details)}
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-4 bg-black text-white hover:bg-[#222] py-4 rounded-full font-semibold uppercase tracking-widest text-[13px] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] shadow-lg shadow-black/10 disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? "Processing..." : "Continue to Payment"}
              </button>

              {error && (
                <div ref={errorRef} className="mt-4 p-4 bg-red-50 text-red-600 rounded-xl flex items-start gap-3">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </form>
          )}

          {step === 2 && (
            <div className="flex flex-col items-center justify-center text-center py-6">
              <div className="bg-[#f8f8f8] w-20 h-20 rounded-full flex items-center justify-center mb-6">
                <CreditCard size={32} className="text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Complete Payment</h3>
              <p className="text-gray-500 mb-8 max-w-sm">
                To confirm your order for {product?.name}, please proceed to online payment via Razorpay secure checkout.
              </p>
              
              <div className="w-full bg-[#f8f8f8] p-4 rounded-xl mb-8 flex justify-between items-center text-sm">
                 <span className="font-semibold text-gray-500 uppercase tracking-widest text-xs">Total Amount:</span>
                 <span className="font-bold text-lg">{activePriceFormatted}</span>
              </div>

              <button
                onClick={handlePayment}
                disabled={isSubmitting}
                className="w-full bg-black text-white hover:bg-[#222] py-4 rounded-full font-semibold uppercase tracking-widest text-[13px] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] shadow-lg shadow-black/10 disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? "Processing..." : "Pay Now"}
              </button>

              {paymentTimeoutMsg && isSubmitting && (
                <div className="mt-4 w-full p-4 bg-orange-50 text-orange-800 border border-orange-200 rounded-xl flex items-start gap-3 text-left">
                  <AlertCircle size={20} className="shrink-0 mt-0.5 text-orange-500" />
                  <p className="text-sm font-medium leading-relaxed">{paymentTimeoutMsg}</p>
                </div>
              )}

              {error && (
                <div ref={errorRef} className="mt-4 w-full p-4 bg-red-50 text-red-600 rounded-xl flex items-start gap-3 text-left">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={() => {
                  trackEvent("click_back_to_details", { item_name: product?.name });
                  setStep(1);
                }}
                className="mt-4 text-xs font-semibold text-gray-500 hover:text-black uppercase tracking-widest underline underline-offset-4 cursor-pointer"
              >
                Back to Details
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center text-center py-6">
              <CheckCircle size={64} className="text-green-500 mb-6" />
              <h3 className="text-2xl font-bold mb-2">Order Confirmed!</h3>
              <p className="text-gray-500 mb-8 max-w-sm text-sm">
                Thank you for your purchase. We have received your order and will begin processing it shortly.
              </p>
              
              <div className="w-full bg-[#f8f8f8] p-5 rounded-2xl mb-8 flex flex-col gap-3 text-sm text-left">
                 <div className="flex justify-between border-b border-gray-200 pb-3">
                   <span className="font-semibold text-gray-500">Order ID:</span>
                   <span className="font-bold">{orderInfo?.orderId}</span>
                 </div>
                 <div className="flex justify-between border-b border-gray-200 pb-3">
                   <span className="font-semibold text-gray-500">Product:</span>
                   <span className="font-medium text-right max-w-[60%]">{product?.name}</span>
                 </div>
                 <div className="flex justify-between">
                   <span className="font-semibold text-gray-500">Delivery To:</span>
                   <span className="font-medium text-right max-w-[60%]">
                     {details.firstName} {details.lastName} <br />
                     {details.address1}, {details.city}
                   </span>
                 </div>
              </div>

              <button
                onClick={() => {
                  trackEvent("click_done_after_purchase", { item_name: product?.name });
                  onClose();
                }}
                className="w-full bg-black text-white hover:bg-[#222] py-4 rounded-full font-semibold uppercase tracking-widest text-[13px] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] shadow-lg shadow-black/10 cursor-pointer"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
